<!-- ── Gradient header ─────────────────────────────────────────────────────── -->
<div class="dialog-header type-header--{{ currentType }}">
  <div class="dialog-header-left">
    <span class="dialog-type-icon">
      <mat-icon>{{ typeIconMap[currentType] }}</mat-icon>
    </span>
    <div class="header-text">
      <span class="dialog-title">{{ isEdit ? 'Edit' : 'New' }} {{ typeLabel(currentType) }}</span>
      <span class="dialog-sub">{{ isEdit ? 'Update task details' : 'Add to your project' }}</span>
    </div>
  </div>
  <button class="hdr-close-btn" mat-dialog-close><mat-icon>close</mat-icon></button>
</div>

<!-- ── Form body ──────────────────────────────────────────────────────────── -->
<div class="dialog-body" [formGroup]="form">

  <!-- Type pills -->
  @if (!data.parentId) {
    <div class="type-selector">
      @for (t of taskTypes; track t) {
        <button type="button" class="type-pill type--{{ t }}"
                [class.active]="currentType === t"
                (click)="form.controls.task_type.setValue(t)">
          <mat-icon>{{ typeIconMap[t] }}</mat-icon>
          {{ typeLabel(t) }}
        </button>
      }
    </div>
  }

  <!-- Title -->
  <div class="f-group">
    <label class="f-label">Title <span class="required">*</span></label>
    <input class="f-input" formControlName="title" placeholder="What needs to be done?" />
    @if (form.controls.title.dirty && form.controls.title.hasError('required')) {
      <span class="f-error">Title is required</span>
    }
  </div>

  <!-- Status chips -->
  <div class="f-group">
    <div class="f-label">Status</div>
    <div class="chip-row">
      @for (s of statuses; track s) {
        <button type="button" class="status-chip status--{{ s }}"
                [class.active]="form.controls.status.value === s"
                (click)="form.controls.status.setValue(s)">{{ statusLabel(s) }}</button>
      }
    </div>
  </div>

  <!-- Priority chips -->
  <div class="f-group">
    <div class="f-label">Priority</div>
    <div class="chip-row">
      @for (p of priorities; track p) {
        <button type="button" class="priority-chip priority--{{ p }}"
                [class.active]="form.controls.priority.value === p"
                (click)="form.controls.priority.setValue(p)">{{ p | titlecase }}</button>
      }
    </div>
  </div>

  <!-- Story points + Due date -->
  @if (!isEpic) {
    <div class="f-row">
      <div class="f-group">
        <label class="f-label">Story Points</label>
        <input class="f-input" type="number" min="0" formControlName="story_points" placeholder="—" />
      </div>
      @if (!isSubtask) {
        <div class="f-group">
          <label class="f-label">Due Date</label>
          <input class="f-input" type="date" formControlName="due_date" />
        </div>
      }
    </div>
  }

  <!-- Assignee -->
  <div class="f-group">
    <div class="f-label">Assignee</div>
    @if (selectedMember) {
      <div class="selected-member">
        @if (selectedMember.profile_picture) {
          <img class="member-avatar" [src]="selectedMember.profile_picture" referrerpolicy="no-referrer" />
        } @else {
          <span class="member-avatar member-avatar--initial">{{ initial(selectedMember.name) }}</span>
        }
        <span class="member-name">{{ selectedMember.name }}</span>
        <button class="clear-btn" type="button" (click)="clearAssignee()">
          <mat-icon>close</mat-icon>
        </button>
      </div>
    } @else {
      <mat-form-field appearance="outline" class="assignee-field" subscriptSizing="dynamic">
        <input matInput [formControl]="assigneeSearchCtrl"
               [matAutocomplete]="memberAuto" placeholder="Search team members…" />
        <mat-icon matSuffix>search</mat-icon>
        <mat-autocomplete #memberAuto (optionSelected)="selectMember($event.option.value)">
          @for (m of filteredMembers$ | async; track m.user_id) {
            <mat-option [value]="m">
              <div class="member-option">
                @if (m.profile_picture) {
                  <img class="opt-avatar" [src]="m.profile_picture" referrerpolicy="no-referrer" />
                } @else {
                  <span class="opt-avatar opt-avatar--initial">{{ initial(m.name) }}</span>
                }
                {{ m.name }}
              </div>
            </mat-option>
          }
        </mat-autocomplete>
      </mat-form-field>
    }
  </div>

  <!-- Sprint -->
  @if (showSprint) {
    <div class="f-group">
      <label class="f-label">Sprint</label>
      <select class="f-select" formControlName="sprint_id">
        <option [ngValue]="null">Backlog (no sprint)</option>
        @for (s of sprints; track s.id) {
          <option [ngValue]="s.id">{{ s.name }}{{ s.status === 'active' ? '  ● Active' : '' }}</option>
        }
      </select>
    </div>
  }

  <!-- Parent Epic -->
  @if (showParentEpic && epicTasks.length > 0) {
    <div class="f-group">
      <label class="f-label">Parent Epic</label>
      <select class="f-select" formControlName="parent_epic_id">
        <option [ngValue]="null">No epic</option>
        @for (e of epicTasks; track e.id) {
          <option [ngValue]="e.id">{{ e.task_key }} — {{ e.title }}</option>
        }
      </select>
    </div>
  }

  <!-- Description -->
  <div class="f-group">
    <label class="f-label">Description</label>
    <textarea class="f-textarea" formControlName="description" rows="3"
              placeholder="Add context or background notes…"></textarea>
  </div>

  <!-- Acceptance Criteria — not shown for subtasks -->
  @if (!isSubtask) {
    <div class="f-group">
      <label class="f-label">
        <span class="ac-label-icon">✓</span> Acceptance Criteria
      </label>
      <textarea class="f-textarea f-textarea--ac" formControlName="acceptance_criteria" rows="4"
                placeholder="- Given … When … Then …&#10;- Must display error when…&#10;- Performance: loads in < 2s"></textarea>
    </div>
  }

  <!-- Labels -->
  @if (!isEpic && !isSubtask) {
    <div class="f-group">
      <div class="f-label">Labels</div>
      <div class="label-chips">
        @for (label of labels; track label) {
          <span class="label-chip">
            {{ label }}<button type="button" class="chip-remove" (click)="removeLabel(label)">✕</button>
          </span>
        }
        <input class="label-input" [formControl]="labelInput"
               placeholder="Type + Enter"
               (keydown.enter)="addLabel($event)" />
      </div>
    </div>
  }

</div>

<!-- ── Footer ─────────────────────────────────────────────────────────────── -->
<div class="dialog-footer">
  @if (saveError) {
    <div class="save-error">
      <mat-icon>error_outline</mat-icon>
      {{ saveError }}
    </div>
  }
  <button class="cancel-btn" mat-dialog-close [disabled]="saving">Cancel</button>
  <button class="save-btn type-save--{{ currentType }}" (click)="save()"
          [disabled]="form.invalid || saving">
    @if (saving) {
      <mat-progress-spinner mode="indeterminate" diameter="16" />
    } @else {
      <mat-icon>{{ isEdit ? 'check' : 'add' }}</mat-icon>
      {{ isEdit ? 'Save Changes' : 'Create ' + typeLabel(currentType) }}
    }
  </button>
</div>
